import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../utils/queryClient'
import { notificationsApi } from '../services/notificationsApi'
import { NOTIFICATIONS_POLL_INTERVAL_MS } from '../config/syncConfig'

// Polls GET /notifications/website every NOTIFICATIONS_POLL_INTERVAL_MS while
// the user is authenticated. Multiple components can call this hook (e.g. the
// bell is mounted in both DesktopHeader and MobileHeader) without doubling
// network traffic — React Query dedupes concurrent useQuery calls that share
// the same queryKey into a single request/interval.
function getIsGuestUser() {
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'))
    return authUser?.data?.is_guest === true || authUser?.is_guest === true
  } catch (err) {
    return false
  }
}

export function useWebsiteNotifications() {
  const isAuthed = !!localStorage.getItem('authToken')
  const isGuest = getIsGuestUser()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: queryKeys.notifications.website({ unreadOnly: false }),
    queryFn: () => notificationsApi.fetchWebsiteNotifications({ page: 1, pageSize: 20 }),
    enabled: isAuthed && !isGuest,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all })

  const markReadMutation = useMutation({
    mutationFn: (id) => notificationsApi.markNotificationRead(id),
    onSuccess: invalidate,
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllNotificationsRead(),
    onSuccess: invalidate,
  })

  return {
    notifications: query.data?.results ?? [],
    unreadCount: query.data?.unread_count ?? 0,
    isLoading: query.isLoading,
    markRead: markReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
  }
}

export default useWebsiteNotifications
